import {
  createContext,
  useContext,
  useState,
  ReactNode,
  FC,
  useEffect,
  useRef,
} from "react";
import { keycloakService, UserInfo } from "@Services/keycloak";
import { LoadingScreen } from "@Components/LoadingScreen";
import { SessionExpiredModal } from "@Components/SessionExpiredModal";
import { tokenAutoRefreshService } from "@Services/tokenAutoRefresh";

interface KeycloakContextType {
  isAuthenticated: boolean;
  userInfo: UserInfo | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  showSessionExpired: () => void;
  pendingNavigation: string | null;
  clearPendingNavigation: () => void;
}

// Exportado para ser consumido diretamente por outros contextos (ex.: CompanyContext).
export const KeycloakContext = createContext<KeycloakContextType | undefined>(
  undefined
);

export const useKeycloak = () => {
  const context = useContext(KeycloakContext);
  if (!context) {
    throw new Error("useKeycloak must be used within KeycloakProvider");
  }
  return context;
};

interface KeycloakProviderProps {
  children: ReactNode;
}


/** Rotas públicas que NÃO devem disparar o redirect automático para o Keycloak. */
const PUBLIC_PATHS = ["/login", "/logout", "/authorization-error"];

// Salva a rota atual como destino de retorno pós-login — mas só se for uma rota
// real do app (não as telas de auth) e não um callback. Assim, ao clicar "Entrar"
// na tela de pré-login/saída não sobrescrevemos a rota salva de onde a sessão caiu.
const saveReturnPath = () => {
  const { pathname, search, hash } = window.location;
  const full = pathname + search + hash;
  if (
    !PUBLIC_PATHS.includes(pathname) &&
    !full.includes("code=") &&
    !full.includes("error=")
  ) {
    localStorage.setItem("login_original_path", full);
  }
};

export const KeycloakProvider: FC<KeycloakProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSessionExpiredModalOpen, setIsSessionExpiredModalOpen] =
    useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(
    null
  );

  const [urlParams] = useState(
    () => new URLSearchParams(window.location.search)
  );

  // Guard contra a dupla-execução de efeitos do React.StrictMode em dev:
  // sem isso o `code` de autorização é trocado 2x e a 2ª troca falha com
  // invalid_grant ("Code not valid").
  const didInit = useRef(false);
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isPublicPath = () => PUBLIC_PATHS.includes(window.location.pathname);

  const initializeAuth = async () => {
    try {
      // Página pública (login/erro): não executa lógica de autenticação nem redireciona.
      if (isPublicPath()) {
        setIsAuthenticated(keycloakService.isAuthenticated());
        setUserInfo(keycloakService.getSavedUserInfo());
        return;
      }

      const code = urlParams.get("code");
      const state = urlParams.get("state");
      const sessionState = urlParams.get(keycloakService.KC_SESSION_STATE_KEY);
      const error = urlParams.get("error");

      if (error) {
        console.error("Authentication error:", error);
        localStorage.removeItem("login_original_path");
        localStorage.removeItem("login_state");
        setTimeout(() => keycloakService.redirectToLogin(), 2000);
        setIsLoading(false);
        return;
      }

      if (code && state && sessionState) {
        await handleKeycloakCallback(code, state, sessionState);
        return;
      }

      // Há sessão se o token é válido OU se existe refresh token para renovar.
      // Sem o "|| refresh token", um access token expirado (após ~5min) mandaria
      // o F5 direto para o login em vez de renovar.
      const hasSession =
        keycloakService.isAuthenticated() ||
        !!localStorage.getItem("login_refresh_token");
      if (hasSession) {
        await loadUserSession();
      } else {
        const path =
          window.location.pathname +
          window.location.search +
          window.location.hash;
        if (!path.includes("code=") && !path.includes("error=")) {
          localStorage.setItem("login_original_path", path);
          // Pré-login: mostra a tela dizendo onde a pessoa está + botão Entrar.
          window.location.href = "/login";
          return;
        }
        setIsAuthenticated(false);
        setUserInfo(null);
      }
    } catch (error) {
      await keycloakService.redirectToLogin();
      setIsAuthenticated(false);
      setUserInfo(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeycloakCallback = async (
    code: string,
    state: string,
    sessionState: string
  ) => {
    try {
      setIsLoading(true);
      localStorage.setItem(keycloakService.KC_SESSION_STATE_KEY, sessionState);

      try {
        await keycloakService.exchangeCodeForTokens(code, state);
      } catch (tokenError) {
        console.error("Token exchange failed:", tokenError);
        localStorage.setItem("authError", "token_exchange_failed");
        localStorage.setItem(
          "authErrorDetail",
          String((tokenError as Error)?.message ?? tokenError)
        );
        window.location.href = "/authorization-error";
        return;
      }

      let info: UserInfo;
      try {
        info = await keycloakService.getUserInfo();
      } catch (userInfoError) {
        console.error("getUserInfo failed:", userInfoError);
        localStorage.setItem("authError", "userinfo_failed");
        localStorage.setItem(
          "authErrorDetail",
          String((userInfoError as Error)?.message ?? userInfoError)
        );
        window.location.href = "/authorization-error";
        return;
      }

      setIsAuthenticated(true);
      setUserInfo(info);
      localStorage.setItem("userName", info.name);
      localStorage.setItem("userEmail", info.email);

      const originalPath = localStorage.getItem("login_original_path") || "/";
      localStorage.removeItem("login_original_path");
      window.history.replaceState({}, document.title, window.location.pathname);
      setPendingNavigation(originalPath);
    } catch (error) {
      console.error("Unexpected callback error:", error);
      await keycloakService.redirectToLogin();
      setIsAuthenticated(false);
      setUserInfo(null);
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserSession = async () => {
    try {
      const isValid = await keycloakService.ensureValidToken();
      if (!isValid) {
        const path =
          window.location.pathname +
          window.location.search +
          window.location.hash;
        if (
          !path.includes("code=") &&
          !path.includes("error=") &&
          !isPublicPath()
        ) {
          localStorage.setItem("login_original_path", path);
          window.location.href = "/login";
          return;
        }
        setIsAuthenticated(false);
        setUserInfo(null);
        return;
      }

      let info: UserInfo | null = keycloakService.getSavedUserInfo();
      if (!info) {
        info = await keycloakService.getUserInfo();
      }

      setIsAuthenticated(true);
      setUserInfo(info);
      localStorage.setItem("userName", info.name);
      localStorage.setItem("userEmail", info.email);
    } catch (error) {
      console.error("Error loading session:", error);
      const path =
        window.location.pathname +
        window.location.search +
        window.location.hash;
      if (
        !path.includes("code=") &&
        !path.includes("error=") &&
        !isPublicPath()
      ) {
        localStorage.setItem("login_original_path", path);
        await keycloakService.redirectToLogin();
        return;
      }
      setIsAuthenticated(false);
      setUserInfo(null);
    }
  };

  const login = async () => {
    try {
      setIsLoading(true);
      // Preserva a rota de retorno já salva (não sobrescreve a partir de /login|/logout).
      saveReturnPath();
      await keycloakService.redirectToLogin();
    } catch (error) {
      console.error("Login error:", error);
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      setIsAuthenticated(false);
      setUserInfo(null);
      // Logout deliberado: não preserva rota (re-login vai para a home).
      localStorage.removeItem("login_original_path");
      await keycloakService.logout();
    } catch (error) {
      console.error("Logout error:", error);
      localStorage.setItem("login_original_path", "/");
      await keycloakService.redirectToLogin();
    }
  };

  const refreshToken = async () => {
    try {
      await keycloakService.refreshToken();
      await loadUserSession();
    } catch (error) {
      console.error("Token refresh error:", error);
      setIsAuthenticated(false);
      setUserInfo(null);
    }
  };

  const showSessionExpired = () => {
    // Queda de sessão: preserva a rota atual para voltar após novo login.
    saveReturnPath();
    setIsSessionExpiredModalOpen(true);
  };

  const handleCloseSessionExpiredModal = () => {
    setIsSessionExpiredModalOpen(false);
    // OK no alerta → encerra a sessão e vai para a tela de saída, PRESERVANDO
    // login_original_path (não usa o logout deliberado, que limparia a rota).
    setIsAuthenticated(false);
    setUserInfo(null);
    keycloakService.logout();
  };

  const clearPendingNavigation = () => setPendingNavigation(null);

  useEffect(() => {
    if (isAuthenticated) {
      tokenAutoRefreshService.start();
    } else {
      tokenAutoRefreshService.stop();
    }
    return () => tokenAutoRefreshService.stop();
  }, [isAuthenticated]);

  // (Removido) Não fazer logout no `beforeunload`: isso deslogava o usuário a cada
  // F5/refresh quando nenhuma empresa estava selecionada. A sessão agora persiste no
  // reload e é renovada via refresh token em `loadUserSession`.

  return (
    <KeycloakContext.Provider
      value={{
        isAuthenticated,
        userInfo,
        login,
        logout,
        refreshToken,
        isLoading,
        setIsLoading,
        showSessionExpired,
        pendingNavigation,
        clearPendingNavigation,
      }}
    >
      {children}
      {isLoading && <LoadingScreen />}
      <SessionExpiredModal
        isOpen={isSessionExpiredModalOpen}
        onClose={handleCloseSessionExpiredModal}
      />
    </KeycloakContext.Provider>
  );
};
